import React, { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import FlexColumn from '@unicons-base/FlexColumn/FlexColumn';
import {
  getItemPlacement,
  isGridCard,
  isHorizontalListCard,
  repeatPlacement
} from '@utils/unacademyIcons/anyfeedHelper';
import { AnyfeedDataProps } from 'types/unacademyIcons/anyfeed.type';
import FlexRow from '@unicons-base/FlexRow/FlexRow';
import Link from 'next/link';
import { mediaQuery, Spacings } from '@styles/index';
import useWindowSize from '@hooks/useWindowSize';
import CourseCarousel from '@unicons-comp/CourseCarousel/CourseCarousel';

const StyledImage = styled.img`
  position: relative;
  width: 100%;
  height: auto;
  min-height: 370px;
  cursor: pointer;
  ${mediaQuery.uptoMobile} {
    min-height: 100%;
  }
`;

const StyledFlexRow = styled(FlexRow)`
  ${mediaQuery.uptoTablet} {
    padding: 0px ${Spacings.SPACING_2B};
  }
`;

const AnyfeedCourse: React.FC<AnyfeedDataProps> = ({ data, list }) => {
  const { isTablet } = useWindowSize();

  const { cardType, itemPlacement, defaultItemPlacement } = data;
  const hasItemPlacement =
    Array.isArray(itemPlacement) && itemPlacement.length > 0;

  const dataPlacementList = useMemo(
    () =>
      repeatPlacement(
        hasItemPlacement ? itemPlacement : [defaultItemPlacement || 1],
        list.length
      ),
    [hasItemPlacement, itemPlacement, defaultItemPlacement, list.length]
  );

  const coverImage = useCallback(
    (placement, course) => {
      if (isHorizontalListCard(cardType)) return course.coverRectangle;
      if (placement > 1 && isTablet) {
        return course.coverPortrait;
      }
      if (isGridCard(cardType) && isTablet) return course.coverPortrait;
      return course.coverLandscape;
    },
    [isTablet, cardType]
  );

  if (isHorizontalListCard(cardType)) {
    return <CourseCarousel courseList={list} />;
  }
  return (
    <StyledFlexRow>
      {list.map((course, index) => {
        const columnWidth = getItemPlacement(
          dataPlacementList[index],
          cardType
        );
        return (
          <FlexColumn key={course.uuid} columnWidth={columnWidth}>
            <Link href={`/icons/courses/${course.slug}`} passHref>
              <StyledImage
                src={coverImage(dataPlacementList[index], course)}
                alt={course.title}
              />
            </Link>
          </FlexColumn>
        );
      })}
    </StyledFlexRow>
  );
};

AnyfeedCourse.displayName = 'AnyfeedCourse';

export default AnyfeedCourse;
